/* eslint-disable no-nested-ternary */
import React, { useMemo, useState } from 'react';
import { Box, Typography, Button, Grid, Skeleton } from '@mui/material';
import { useTheme } from '@emotion/react';
import { Element } from 'react-scroll';

import useClasses from '../../../../../hooks/legacy/useClasses';

import styles from './RestaurantMainView.style';
import RestaurantCard from '../RestaurantCard';
import { getConfig } from '../../../../../AppConfig';
import { getImage, getText } from '../../../../../util/Helpers';
import HomeSection from '../../../../layout/HomeSection';
import RestaurantContactView from '../RestaurantContactView';

const SPLIT_TEXT_REGEX = /\\r\\n|\\r|\\n/;

function RestaurantMainView() {
  const classes = useClasses(styles);
  const theme = useTheme();
  const config = getConfig();

  const [learnMoreActive, setLearnMoreActive] = useState(false);

  const background = getImage(config, 'about_bg', '');

  const hasFeature = getText(config, 'has_featured') === 'true';

  const { images, locales } = config;

  const menuData = useMemo(() => {
    if (images && locales) {
      const result = images
        .filter((item) => item.name === 'menu_sample') // Filter items with name "menu_sample"
        .map((item) => {
          const { version } = item;
          const titleKey = `section_${String(version).padStart(2, '0')}_title`;
          const titleData = locales.find((locale) => locale.name === titleKey);

          return {
            image: item.url ? item.url : '',
            title: titleData ? titleData.text : '',
          };
        });

      return result;
    }
    return null;
  }, [images, locales]);

  return (
    <Box data-test-id="restaurantMainView" sx={{ pl: 0, pr: 0 }}>
      <HomeSection className="viewElement" />
      <Element name="aboutUs" className="viewElement">
        {background ? (
          <Box
            component="img"
            sx={{ height: '500px', width: '100%' }}
            src={
              background?.includes('https') ? background : `https:${background}`
            }
            className={classes.restaurantImg}
          />
        ) : (
          <Skeleton variant="rectangular" height="500px" width="100%" />
        )}
        <Box
          sx={{
            mb: 0,
            ml: '10%',
            mt: -37,
            mr: '10%',
            px: 0,
            py: 0,
            position: 'relative',
            background: 'white',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            [theme.breakpoints.down('sm')]: {
              ml: 2,
              mr: 2,
              px: 0,
              py: 0,
              my: 1,
            },
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
          }}
          height="auto"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'center',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 18,
              [theme.breakpoints.down('sm')]: {
                mb: 3,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'center',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 4,
                mt: 4,
              }}
            >
              <Typography variant="h5" color="black">
                {getText(
                  config,
                  'about_us_title',
                  config.textsPlaceholder.restaurant_about_us_title
                )}
              </Typography>
            </Box>
            <Box
              sx={{
                pl: 6,
                pr: 6,
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'center',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {getText(
                config,
                'about_us_lead',
                config.textsPlaceholder.restaurant_about_us_lead
              )
                .split(SPLIT_TEXT_REGEX)
                .map((textBlock) =>
                  textBlock.trim().length > 0 ? (
                    <Typography
                      variant="body1"
                      color="black"
                      className="elementText"
                    >
                      {textBlock.trim().length > 0 ? textBlock.trim() : ' '}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body1"
                      color="black"
                      className="elementText"
                    >
                      &nbsp;
                    </Typography>
                  )
                )}
            </Box>
            <Box
              sx={{
                pl: 6,
                pr: 6,
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'center',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {learnMoreActive &&
                getText(
                  config,
                  'about_us_desc',
                  config.textsPlaceholder.restaurant_about_us_desc
                )
                  .split(SPLIT_TEXT_REGEX)
                  .map((textBlock) =>
                    textBlock.trim().length > 0 ? (
                      <Typography
                        variant="body1"
                        color="black"
                        className="elementText"
                      >
                        {textBlock.trim().length > 0 ? textBlock.trim() : ' '}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body1"
                        color="black"
                        className="elementText"
                      >
                        &nbsp;
                      </Typography>
                    )
                  )}
            </Box>
            <Box sx={{ mt: 4, mb: 4 }}>
              {getText(
                config,
                'about_us_desc',
                config.textsPlaceholder.restaurant_about_us_desc
              ) !== '' && (
                <Button
                  color="primary"
                  variant="contained"
                  data-test-id="learn-more"
                  className={classes.button}
                  onClick={() => setLearnMoreActive(!learnMoreActive)}
                >
                  {learnMoreActive ? 'View Less' : 'View More'}
                </Button>
              )}
            </Box>
          </Box>
          {hasFeature ? (
            <Box sx={{ mt: 4, mb: 16 }}>
              {menuData?.length ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 4,
                    justifyContent: 'center',
                    [theme.breakpoints.down('sm')]: {
                      flexDirection: 'column',
                      alignItems: 'center',
                    },
                  }}
                >
                  {menuData.map((rData) => (
                    <RestaurantCard
                      restaurant={rData}
                      key={`restaurantCard_${rData.title}`}
                    />
                  ))}
                </Box>
              ) : (
                <Grid
                  container
                  spacing={2}
                  sx={{
                    flexGrow: 1,
                    [theme.breakpoints.down('sm')]: {
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    },
                  }}
                >
                  {Array.from(Array(3).keys()).map((x) => (
                    <Grid item key={`skeleton-${x}`} xs={12} md={6} lg={4}>
                      <Skeleton
                        height={140}
                        width={320}
                        variant="rectangular"
                        sx={{ borderRadius: 2 }}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          ) : null}
        </Box>
      </Element>
      <Element name="hours" className="viewElement">
        <RestaurantContactView />
      </Element>
    </Box>
  );
}

export default RestaurantMainView;
